import { useEffect, useState } from "react";
import { useFormState } from "react-hook-form";

/**
 * Resolves nested API errors by following the given key path.
 * @param {any} errors - The error object from the API.
 * @param {string[]} [keys=[]] - The list of keys to traverse the error object.
 * @returns {string | null | object} - The resolved error message or null if not found.
 */
function resolveApiError(errors, keys = []) {
  if (errors && Object.keys(errors).length === 0) {
    return null;
  }
  if (keys.length === 0 && typeof errors === "object") {
    return errors;
  }
  if (typeof errors[keys[0]] === "object") {
    const newKeys = [...keys];
    newKeys.shift();
    return resolveApiError(errors[keys[0]], newKeys);
  }
  if (typeof errors[keys[0]] === "string") {
    return errors[keys[0]];
  }
  return null;
}

/**
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {import("react-hook-form").FieldPath<TFieldValues>} TName
 * @typedef {object} Props
 * @prop {import("react-hook-form").Control<TFieldValues>} control - The form control object.
 * @prop {TName} name - The name of the form field to observe for errors.
 * @prop {(params: { hasError: boolean, error: any }) => import("react").ReactNode} render - Optional render function that receives error state and renders accordingly.
 */
/**
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {import("react-hook-form").FieldPath<TFieldValues>} TName
 * @param {Props<TFieldValues, TName>} props
 */
function FormErrorObserver({ control, name, render }) {
  const { errors } = useFormState({ control, name });

  const [lastError, setLastError] = useState(/** @type {any} */ (null));

  const currentError = resolveApiError(errors, name.split("."));

  const hasError = Boolean(currentError);

  useEffect(() => {
    if (currentError) {
      setLastError(currentError);
    }
  }, [currentError]);

  return (
    render?.({ hasError, error: currentError || lastError || null }) ?? null
  );
}

export default FormErrorObserver;
